'use client'

import classNames from 'clsx'
import { useCallback, useEffect, useId, useMemo, useState } from 'react'

import CheckIcon from '@/assets/icons/check-sm.svg'
import ChevronDown from '@/assets/icons/leaderboard/lb-chevron-down-16.svg'
import useClickOutside from '@/hooks/useClickOutside'

import type { FilterOption } from './FilterDropdown'
import S from './FilterDropdown.module.scss'

type MultiFilterDropdownProps = {
  /** Picked values; empty — «all». */
  values: string[]
  onChange: (values: string[]) => void
  options: ReadonlyArray<FilterOption>
  /** Trigger text with nothing picked, e.g. «All sports». */
  allLabel: string
  /** Accessible name of the trigger and the list, e.g. «Sport». */
  label: string
  /** A search box over the options (BAT-1110: sports and countries). */
  searchPlaceholder?: string
  noMatchesLabel?: string
}

/**
 * BAT-1110: a leaderboard filter with several values (Figma "LB Filter",
 * the badge counts the picks). «All» clears the picks; the menu stays open
 * while picking.
 */
export default function MultiFilterDropdown({
  values,
  onChange,
  options,
  allLabel,
  label,
  searchPlaceholder,
  noMatchesLabel
}: MultiFilterDropdownProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
  }, [])
  const ref = useClickOutside<HTMLDivElement>(open, close)
  const listId = useId()

  useEffect(() => {
    if (!open) {
      return
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  const picked = useMemo(() => new Set(values), [values])
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q
      ? options.filter(option => option.label.toLowerCase().includes(q))
      : options
  }, [options, query])

  // The first pick names the filter; the badge counts all of them (the
  // design's «All sports ②» read as if nothing were picked).
  const triggerText =
    values.length === 0
      ? allLabel
      : options.find(option => option.value === values[0])?.label ?? allLabel

  const toggle = (value: string) =>
    onChange(
      picked.has(value)
        ? values.filter(item => item !== value)
        : [...values, value]
    )

  return (
    <div className={classNames(S.dropdown, S.dropdownMulti)} ref={ref}>
      <button
        type="button"
        className={classNames(
          S.trigger,
          open && S.triggerOpen,
          values.length > 0 && S.triggerActive
        )}
        aria-label={`${label}: ${
          values.length > 1
            ? `${triggerText} +${values.length - 1}`
            : triggerText
        }`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className={S.triggerText}>{triggerText}</span>
        {values.length > 1 ? (
          <span className={S.badge} aria-hidden>
            {values.length}
          </span>
        ) : null}
        <ChevronDown
          className={classNames(S.chevron, open && S.chevronOpen)}
          aria-hidden
        />
      </button>

      {open ? (
        <div className={classNames(S.menu, S.menuMulti)}>
          {searchPlaceholder ? (
            <input
              type="search"
              className={S.menuSearch}
              value={query}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              onChange={event => setQuery(event.target.value)}
              autoFocus
            />
          ) : null}
          <ul
            id={listId}
            className={S.menuList}
            role="listbox"
            aria-label={label}
            aria-multiselectable="true"
          >
            {query.trim() ? null : (
              <li role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={values.length === 0}
                  className={classNames(
                    S.option,
                    values.length === 0 && S.optionSelected
                  )}
                  onClick={() => onChange([])}
                >
                  <span className={S.optionLabel}>{allLabel}</span>
                  {values.length === 0 ? (
                    <CheckIcon className={S.check} aria-hidden />
                  ) : null}
                </button>
              </li>
            )}
            {visible.map(option => {
              const isSelected = picked.has(option.value)
              return (
                <li key={option.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={classNames(
                      S.option,
                      isSelected && S.optionSelected
                    )}
                    onClick={() => toggle(option.value)}
                  >
                    <span className={S.optionLabel}>{option.label}</span>
                    {isSelected ? (
                      <CheckIcon className={S.check} aria-hidden />
                    ) : null}
                  </button>
                </li>
              )
            })}
            {visible.length === 0 && noMatchesLabel ? (
              <li className={S.noMatches}>{noMatchesLabel}</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
